/**
 * Environment-Aware API Service Layer
 * Stage 4: Backend-Frontend Integration
 */

import { 
  GenerateAvatarRequest, 
  GenerateAvatarResponse, 
  RateLimitResponse
} from '../types/api';
import { getCachedUserIP } from '../utils/ipDetection';

export class ApiServiceError extends Error {
  public status?: number;
  public code?: string;
  public details?: any;

  constructor(message: string, status?: number, code?: string, details?: any) {
    super(message);
    this.name = 'ApiServiceError';
    this.status = status;
    this.code = code;
    this.details = details;
  }
}

export class ApiService {
  private baseUrl: string;
  private timeout: number;

  constructor() {
    // Automatically detect environment and set appropriate URL
    this.baseUrl = this.getApiBaseUrl();
    this.timeout = 300000; // 5 minutes for avatar generation
    
    // console.log('ApiService initialized:', {
    //   baseUrl: this.baseUrl,
    //   environment: process.env.NODE_ENV,
    //   timeout: this.timeout
    // });
  }

  /**
   * Get API base URL based on environment
   */
  private getApiBaseUrl(): string {
    // Check if we're in development mode
    if (process.env.NODE_ENV === 'development' || 
        window.location.hostname === 'localhost' ||
        window.location.hostname === '127.0.0.1') {
      return 'http://localhost:3001/local'; // Local serverless-offline with stage
    }
    
    // Production environment
    const productionUrl = process.env.REACT_APP_API_GATEWAY_URL;
    if (!productionUrl) {
      throw new Error('REACT_APP_API_GATEWAY_URL is not configured for production');
    }
    
    return productionUrl;
  }

  /**
   * Make HTTP request with retry logic and timeout
   */
  private async makeRequest<T>(
    endpoint: string, 
    options: RequestInit = {},
    retries: number = 2
  ): Promise<T> {
    const url = `${this.baseUrl}${endpoint}`;
    
    const defaultOptions: RequestInit = {
      headers: {
        'Content-Type': 'application/json',
        'Accept': 'application/json',
      },
      ...options
    };

    for (let attempt = 0; attempt <= retries; attempt++) {
      try {
        // console.log(`API Request (attempt ${attempt + 1}):`, {
        //   method: defaultOptions.method || 'GET',
        //   url,
        //   headers: defaultOptions.headers
        // });

        // Create a timeout promise
        const timeoutPromise = new Promise<never>((_, reject) => {
          setTimeout(() => reject(new Error('Request timeout')), this.timeout);
        });

        // Race between fetch and timeout
        const response = await Promise.race([
          fetch(url, defaultOptions),
          timeoutPromise
        ]);
        
        // Handle different response types
        let responseData: any;
        const contentType = response.headers.get('content-type');
        
        if (contentType && contentType.includes('application/json')) {
          responseData = await response.json();
        } else {
          responseData = { 
            message: await response.text(),
            status: response.status 
          };
        }

        // console.log(`API Response (attempt ${attempt + 1}):`, {
        //   status: response.status,
        //   ok: response.ok,
        //   data: responseData
        // });

        if (!response.ok) {
          throw new ApiServiceError(
            responseData.error || responseData.message || `HTTP ${response.status}`,
            response.status,
            responseData.code,
            responseData
          );
        }

        return responseData as T;

      } catch (error) {
        console.error(`API Request failed (attempt ${attempt + 1}):`, error);
        
        // Enhanced error classification and handling
        const enhancedError = this.classifyError(error);
        
        // Don't retry on client errors (4xx) or certain specific errors
        if (enhancedError.status && enhancedError.status >= 400 && enhancedError.status < 500) {
          throw enhancedError;
        }
        
        // Don't retry on rate limit errors
        if (enhancedError.code === 'RATE_LIMIT_EXCEEDED') {
          throw enhancedError;
        }
        
        // Retry on network errors, timeouts, and server errors (5xx)
        if (attempt < retries && this.shouldRetry(enhancedError)) {
          const delay = this.calculateBackoffDelay(attempt, enhancedError);
          // console.log(`Retrying in ${delay}ms... (Error: ${enhancedError.code})`);
          await new Promise(resolve => setTimeout(resolve, delay));
          continue;
        }
        
        // Final attempt failed
        throw enhancedError;
      }
    }

    throw new ApiServiceError('Maximum retries exceeded', 0, 'MAX_RETRIES_EXCEEDED');
  }

  /**
   * Classify errors for better handling and user feedback
   */
  private classifyError(error: any): ApiServiceError {
    // Already classified API service error
    if (error instanceof ApiServiceError) {
      return error;
    }

    // Timeout errors
    if (error.message === 'Request timeout' || error.name === 'TimeoutError') {
      return new ApiServiceError(
        'Request timed out. Avatar generation can take up to 5 minutes.',
        408,
        'TIMEOUT_ERROR',
        error
      );
    }

    // Network connectivity errors
    if (error.message === 'Failed to fetch' || error.name === 'NetworkError' || error.code === 'NETWORK_ERROR') {
      return new ApiServiceError(
        'Unable to connect to the server. Please check your internet connection.',
        0,
        'NETWORK_ERROR',
        error
      );
    }

    // CORS errors
    if (error.message.includes('CORS') || error.message.includes('cross-origin')) {
      return new ApiServiceError(
        'Cross-origin request blocked. Please try refreshing the page.',
        0,
        'CORS_ERROR',
        error
      );
    }

    // Lambda function timeout (common in avatar generation)
    if (error.message.includes('Task timed out') || error.message.includes('Lambda timeout')) {
      return new ApiServiceError(
        'Server processing timed out. Complex avatars may take longer to generate.',
        504,
        'LAMBDA_TIMEOUT',
        error
      );
    }

    // S3 upload/download errors
    if (error.message.includes('S3') || error.message.includes('upload') || error.message.includes('storage')) {
      return new ApiServiceError(
        'File storage error. Please try again.',
        503,
        'STORAGE_ERROR',
        error
      );
    }

    // DynamoDB errors (rate limiting database)
    if (error.message.includes('DynamoDB') || error.message.includes('database')) {
      return new ApiServiceError(
        'Database error. Please try again in a moment.',
        503,
        'DATABASE_ERROR',
        error
      );
    }

    // Replicate API errors
    if (error.message.includes('Replicate') || error.message.includes('model')) {
      return new ApiServiceError(
        'AI model error. Please try with a different prompt.',
        503,
        'MODEL_ERROR',
        error
      );
    }

    // OpenAI API errors (prompt enhancement)
    if (error.message.includes('OpenAI') || error.message.includes('prompt enhancement')) {
      return new ApiServiceError(
        'Prompt enhancement failed. Using basic prompt instead.',
        503,
        'ENHANCEMENT_ERROR',
        error
      );
    }

    // Rate limiting errors
    if (error.message.includes('rate limit') || error.message.includes('too many requests')) {
      return new ApiServiceError(
        'Rate limit exceeded. Please wait before trying again.',
        429,
        'RATE_LIMIT_EXCEEDED',
        error
      );
    }

    // Generic server errors
    if (error.status >= 500) {
      return new ApiServiceError(
        'Server error. Our team has been notified.',
        error.status,
        'SERVER_ERROR',
        error
      );
    }

    // Default unknown error
    return new ApiServiceError(
      error.message || 'An unexpected error occurred.',
      error.status || 0,
      'UNKNOWN_ERROR',
      error
    );
  }

  /**
   * Determine if an error should be retried
   */
  private shouldRetry(error: ApiServiceError): boolean {
    const retryableCodes = [
      'TIMEOUT_ERROR',
      'NETWORK_ERROR',
      'LAMBDA_TIMEOUT',
      'STORAGE_ERROR',
      'DATABASE_ERROR',
      'MODEL_ERROR',
      'SERVER_ERROR'
    ];

    return retryableCodes.includes(error.code || '');
  }

  /**
   * Calculate backoff delay based on error type and attempt
   */
  private calculateBackoffDelay(attempt: number, error: ApiServiceError): number {
    const baseDelay = 1000; // 1 second
    const exponentialDelay = Math.pow(2, attempt) * baseDelay;

    // Longer delays for certain error types
    switch (error.code) {
      case 'LAMBDA_TIMEOUT':
      case 'MODEL_ERROR':
        return exponentialDelay * 2; // Longer delay for processing-heavy operations
      case 'STORAGE_ERROR':
      case 'DATABASE_ERROR':
        return exponentialDelay * 1.5; // Medium delay for infrastructure issues
      case 'NETWORK_ERROR':
        return Math.min(exponentialDelay, 5000); // Cap network retries at 5 seconds
      default:
        return exponentialDelay;
    }
  }

  /**
   * Generate avatars using enhanced prompts
   */
  async generateAvatars(
    prompt: string, 
    filters: any, 
    uploadedImage?: string
  ): Promise<GenerateAvatarResponse> {
    try {
      // Get user IP for rate limiting
      const userIP = await getCachedUserIP();
      
      // Convert hair array to comma-separated string for backend
      const backendFilters = {
        ...filters,
        hair: Array.isArray(filters.hair) && filters.hair.length > 0
          ? filters.hair.join(',')
          : 'any'
      };
      
      const request: GenerateAvatarRequest = {
        prompt: prompt.trim(),
        filters: backendFilters as any, // Backend expects hair as string, frontend uses string[]
        uploadedImage,
        userIP
      };

      // console.log('Generating avatars:', {
      //   prompt: request.prompt,
      //   filters: request.filters,
      //   hasUploadedImage: !!uploadedImage,
      //   userIP: userIP
      // });

      const response = await this.makeRequest<GenerateAvatarResponse>(
        '/generate',
        {
          method: 'POST',
          body: JSON.stringify(request)
        }
      );

      return response;

    } catch (error) {
      console.error('Avatar generation failed:', error);
      
      if (error instanceof ApiServiceError) {
        throw error;
      }
      
      throw new ApiServiceError(
        `Avatar generation failed: ${error instanceof Error ? error.message : 'Unknown error'}`,
        500,
        'GENERATION_ERROR',
        error
      );
    }
  }

  /**
   * Check rate limit status for current user
   */
  async checkRateLimit(): Promise<RateLimitResponse> {
    try {
      const userIP = await getCachedUserIP();
      
      // console.log('Checking rate limit for IP:', userIP);

      const response = await this.makeRequest<RateLimitResponse>(
        `/rate-limit/${encodeURIComponent(userIP)}`,
        {
          method: 'GET'
        }
      );

      return response;

    } catch (error) {
      console.error('Rate limit check failed:', error);
      
      if (error instanceof ApiServiceError) {
        throw error;
      }
      
      throw new ApiServiceError(
        `Rate limit check failed: ${error instanceof Error ? error.message : 'Unknown error'}`,
        500,
        'RATE_LIMIT_CHECK_ERROR',
        error
      );
    }
  }

  /**
   * Reset rate limit for current user
   */
  async resetRateLimit(): Promise<{ success: boolean; message: string }> {
    try {
      const userIP = await getCachedUserIP();
      
      // console.log('Resetting rate limit for IP:', userIP);

      const response = await this.makeRequest<{ 
        success: boolean; 
        message: string; 
        ip: string;
        timestamp: string;
      }>(
        `/rate-limit/${encodeURIComponent(userIP)}`,
        {
          method: 'DELETE'
        }
      );

      return response;

    } catch (error) {
      console.error('Rate limit reset failed:', error);
      
      if (error instanceof ApiServiceError) {
        throw error;
      }
      
      throw new ApiServiceError(
        `Rate limit reset failed: ${error instanceof Error ? error.message : 'Unknown error'}`,
        500,
        'RATE_LIMIT_RESET_ERROR',
        error
      );
    }
  }

  /**
   * Health check endpoint
   */
  async healthCheck(): Promise<{ 
    status: string; 
    timestamp: string; 
    environment?: string;
    services?: any;
    version?: string;
  }> {
    try {
      const response = await this.makeRequest<{ 
        status: string; 
        timestamp: string; 
        environment?: string;
        services?: any;
        version?: string;
      }>(
        '/health',
        {
          method: 'GET'
        },
        0 // No retries for health check
      );

      return response;

    } catch (error) {
      throw new ApiServiceError(
        `Health check failed: ${error instanceof Error ? error.message : 'Unknown error'}`,
        0,
        'HEALTH_CHECK_ERROR',
        error
      );
    }
  }

  /**
   * Get current API configuration info
   */
  getApiInfo() {
    return {
      baseUrl: this.baseUrl,
      timeout: this.timeout,
      environment: process.env.NODE_ENV,
      isLocal: this.baseUrl.includes('localhost')
    };
  }

  /**
   * Publish submit event to Slack via backend
   */
  async publishSubmitToSlack(data: {
    userName: string;
    email: string;
    generationId: string;
    userPrompt: string;
    enhancedPrompt?: string;
    userFilterSelection?: any;
    usedImageAnalysis?: boolean;
    userIP: string;
    timestamp: string;
    imagesUrl: string[];
  }): Promise<{ success: boolean; message: string }> {
    try {
      const requestData = {
        eventType: 'submit',
        ...data
      };

      const response = await this.makeRequest<{ 
        success: boolean; 
        message: string;
      }>(
        '/publish-slack',
        {
          method: 'POST',
          body: JSON.stringify(requestData)
        },
        0 // No retries for Slack publishing
      );

      return response;

    } catch (error) {
      console.error('Failed to publish submit event to Slack:', error);
      
      if (error instanceof ApiServiceError) {
        throw error;
      }
      
      throw new ApiServiceError(
        `Failed to publish submit event: ${error instanceof Error ? error.message : 'Unknown error'}`,
        500,
        'SLACK_PUBLISH_ERROR',
        error
      );
    }
  }
}

// Singleton instance
export const apiService = new ApiService();
